import React, { useState, useEffect } from "react";
import {
  X, Upload, Loader2, CheckCircle2, AlertCircle, ArrowRight, FileSpreadsheet, Eye, Sparkles,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { STATI_CLIENTE } from "@/lib/format";

/**
 * Excel import wizard with manual field mapping.
 *
 * Steps:
 *  1) upload  -> POST /api/excel-import/preview returns headers + sample + suggestedMapping + targetFields
 *  2) mapping -> user maps each column to a target field
 *  3) options -> default stato/fonte/recall today
 *  4) import  -> POST /api/excel-import/import returns inserted/skipped/total
 */
export default function ExcelImportDialog({ open, onClose, onImported }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({}); // {colIdx: targetKey}
  const [defaults, setDefaults] = useState({ stato: "", fonte: "", recallToday: false, portale: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  // Multi-sheet support: foglio anagrafiche + foglio updates (timeline storica)
  const [anagSheet, setAnagSheet] = useState("");
  const [anagHeaderRow, setAnagHeaderRow] = useState(0);
  const [updatesSheet, setUpdatesSheet] = useState(""); // "" = non importare
  const [updatesHeaderRow, setUpdatesHeaderRow] = useState(0);
  const [updatesMapping, setUpdatesMapping] = useState({ name_col: 1, date_col: 5, content_col: 6, user_col: 4 });
  const [updatesHeaders, setUpdatesHeaders] = useState([]);
  const [uploadPct, setUploadPct] = useState(0);

  useEffect(() => {
    if (!open) {
      // reset on close
      setStep(1); setFile(null); setPreview(null); setMapping({}); setResult(null);
      setDefaults({ stato: "", fonte: "", recallToday: false, portale: "" });
      setAnagSheet(""); setAnagHeaderRow(0); setUpdatesSheet(""); setUpdatesHeaderRow(0);
      setUpdatesMapping({ name_col: 1, date_col: 5, content_col: 6, user_col: 4 });
      setUpdatesHeaders([]);
    }
  }, [open]);

  if (!open) return null;

  const onUpload = async (f) => {
    if (!f) return;
    setFile(f);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/excel-import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data);
      setMapping(r.data.suggestedMapping || {});
      // Multi-sheet detection (auto-popolato)
      setAnagSheet(r.data.detectedAnagSheet || (r.data.sheetNames || [""])[0]);
      setAnagHeaderRow(r.data.detectedAnagHeaderRow || 0);
      setUpdatesSheet(r.data.detectedUpdatesSheet || "");
      setUpdatesHeaderRow(r.data.detectedUpdatesHeaderRow || 0);
      setUpdatesHeaders(r.data.detectedUpdatesHeaders || []);
      // Auto-mapping updates (col Item Name, Created At, Update Content, User)
      const uH = r.data.detectedUpdatesHeaders || [];
      const find = (predicate) => uH.findIndex(predicate);
      setUpdatesMapping({
        name_col: find((h) => /item name|name/i.test(h || "")),
        date_col: find((h) => /created at|date|data/i.test(h || "")),
        content_col: find((h) => /update content|content|note/i.test(h || "")),
        user_col: find((h) => /^user$|utente|operatore/i.test(h || "")),
      });
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore lettura file");
    } finally { setLoading(false); }
  };

  // Cambia il foglio attivo nelle anagrafiche
  const switchAnagSheet = (sheetName) => {
    setAnagSheet(sheetName);
    const sh = (preview?.sheets || []).find((s) => s.name === sheetName);
    if (sh) {
      setAnagHeaderRow(sh.headerRow || 0);
      setMapping(sh.suggestedMapping || {});
      // aggiorna anche il preview "appiattito" (per la tabella di mapping)
      setPreview((p) => ({ ...p,
        headers: sh.headers,
        sample: sh.sample,
        totalRows: sh.totalRows,
      }));
    }
  };

  const setColMapping = (colIdx, target) => {
    setMapping((prev) => {
      const next = { ...prev };
      if (!target) delete next[colIdx];
      else next[colIdx] = target;
      return next;
    });
  };

  const importNow = async () => {
    setLoading(true);
    setUploadPct(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("mapping", JSON.stringify(mapping));
      if (defaults.stato) fd.append("default_stato", defaults.stato);
      if (defaults.fonte) fd.append("default_fonte", defaults.fonte);
      if (defaults.recallToday) fd.append("default_recall_today", "true");
      if (defaults.portale) fd.append("default_portale", defaults.portale);
      // Multi-sheet
      if (anagSheet) fd.append("sheet_name", anagSheet);
      fd.append("header_row", String(anagHeaderRow || 0));
      if (updatesSheet) {
        fd.append("updates_sheet", updatesSheet);
        fd.append("updates_header_row", String(updatesHeaderRow || 0));
        fd.append("updates_mapping", JSON.stringify(updatesMapping));
      }
      const r = await api.post("/excel-import/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 10 * 60 * 1000, // 10 min per file molto grandi (>10k righe)
        onUploadProgress: (e) => {
          if (e.total) {
            const pct = Math.round((e.loaded / e.total) * 100);
            // mostriamo solo upload progress; il processing lato server non e' streamabile
            setUploadPct(pct);
          }
        },
      });
      setResult(r.data);
      setStep(4);
      const upMsg = r.data.updates ? ` + ${r.data.updates.imported} aggiornamenti su ${r.data.updates.clientsTouched} clienti` : "";
      toast.success(`Importati ${r.data.inserted} clienti${upMsg}`);
      onImported?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore import");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="xlsx-import-dialog">
      <div className="bg-white shadow-[0_24px_64px_rgba(10,25,47,0.18)] w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden" style={{ border: "1px solid #E8ECF2", borderRadius: 6 }}>
        <div className="px-6 py-4 border-b border-[#F0F2F5] flex items-center justify-between bg-gradient-to-r from-[#0A192F] to-[#172A45] text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-[#172A45] border border-[#23375a] flex items-center justify-center text-[#B7955B]">
              <FileSpreadsheet className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#B7955B]">Import Excel — Wizard</div>
              <h2 className="font-serif text-xl">{
                step === 1 ? "1. Carica il file" :
                step === 2 ? "2. Mappa le colonne" :
                step === 3 ? "3. Opzioni e conferma" :
                "4. Import completato"
              }</h2>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-white/10 rounded-sm"><X className="w-5 h-5" /></button>
        </div>

        {/* Stepper */}
        <div className="px-6 py-3 border-b border-[#F0F2F5] bg-slate-50">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            {[1, 2, 3, 4].map((s) => (
              <React.Fragment key={s}>
                <div className={`flex items-center gap-2 ${step === s ? "text-[#0A192F] font-medium" : step > s ? "text-emerald-600" : ""}`}>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] tabular-nums ${
                    step === s ? "bg-[#0A192F] text-white" :
                    step > s ? "bg-emerald-500 text-white" :
                    "bg-slate-200 text-slate-500"
                  }`}>{step > s ? "✓" : s}</div>
                  <span>{s === 1 ? "File" : s === 2 ? "Mappa" : s === 3 ? "Conferma" : "Fine"}</span>
                </div>
                {s < 4 && <div className="flex-1 h-px bg-slate-200" />}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {/* STEP 1: Upload */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="text-sm text-slate-600 leading-relaxed">
                Carica un file Excel <code className="bg-slate-100 px-1.5 py-0.5 rounded text-xs">.xlsx</code> con i tuoi clienti.
                Nei prossimi step potrai <strong>mappare ogni colonna</strong> al campo corretto del CRM.
              </div>
              <label className="block cursor-pointer">
                <input
                  type="file"
                  accept=".xlsx,.xls,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={(e) => onUpload(e.target.files?.[0])}
                  data-testid="xlsx-file-input"
                  disabled={loading}
                  className="hidden"
                />
                <div className="flex flex-col items-center gap-3 py-12 border-2 border-dashed border-slate-300 rounded-sm hover:border-[#B7955B] hover:bg-slate-50 transition">
                  {loading ? (
                    <>
                      <Loader2 className="w-8 h-8 animate-spin text-[#B7955B]" />
                      <div className="text-sm text-slate-700">Lettura file in corso...</div>
                    </>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-[#B7955B]" />
                      <div className="text-sm font-medium text-slate-900">Clicca per selezionare il file Excel</div>
                      <div className="text-xs text-slate-500">o trascina qui il file (.xlsx)</div>
                    </>
                  )}
                </div>
              </label>
              <div className="text-[11px] text-slate-500 leading-relaxed bg-slate-50 border border-slate-200 rounded-sm p-3">
                <strong>Suggerimento:</strong> il sistema riconosce automaticamente colonne come "Nome", "Cognome", "Telefono", "Email", "Citta", "CF", "Note", ecc.
                Le date supportano formati <code>gg/mm/aaaa</code>, <code>aaaa-mm-gg</code>, <code>gg-mm-aaaa</code>.
                I duplicati per telefono/CF vengono <strong>aggiornati</strong>, non duplicati.
              </div>
            </div>
          )}

          {/* STEP 2: Mapping */}
          {step === 2 && preview && (
            <div className="space-y-4">
              {/* Multi-sheet selector */}
              {(preview.sheetNames || []).length > 1 && (
                <div className="bg-[#F5EFE3]/40 border border-[#B7955B]/40 rounded-sm p-3 space-y-2">
                  <div className="text-[10px] uppercase tracking-wider text-[#8a6e3d] font-semibold flex items-center gap-1.5">
                    <FileSpreadsheet className="w-3 h-3" /> File con più fogli — scegli cosa importare
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-slate-600">Foglio Anagrafiche</label>
                      <select value={anagSheet} onChange={(e) => switchAnagSheet(e.target.value)}
                              data-testid="xlsx-anag-sheet" className="w-full mt-1 px-2 py-1.5 text-sm border border-slate-300 rounded-sm">
                        {(preview.sheets || []).map((s) => (
                          <option key={s.name} value={s.name}>{s.name} — {s.totalRows} righe ({s.role})</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-slate-600">Foglio Aggiornamenti (opz.)</label>
                      <select value={updatesSheet}
                              onChange={(e) => {
                                setUpdatesSheet(e.target.value);
                                const sh = (preview.sheets || []).find((s) => s.name === e.target.value);
                                if (sh) {
                                  setUpdatesHeaderRow(sh.headerRow || 0);
                                  setUpdatesHeaders(sh.headers || []);
                                }
                              }}
                              data-testid="xlsx-updates-sheet" className="w-full mt-1 px-2 py-1.5 text-sm border border-slate-300 rounded-sm">
                        <option value="">— Non importare aggiornamenti —</option>
                        {(preview.sheets || []).filter((s) => s.name !== anagSheet).map((s) => (
                          <option key={s.name} value={s.name}>{s.name} — {s.totalRows} righe ({s.role})</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {updatesSheet && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs pt-2 border-t border-[#B7955B]/30">
                      <UpdateColPicker label="Nome cliente" value={updatesMapping.name_col} headers={updatesHeaders}
                        onChange={(v) => setUpdatesMapping({ ...updatesMapping, name_col: v })} />
                      <UpdateColPicker label="Data" value={updatesMapping.date_col} headers={updatesHeaders}
                        onChange={(v) => setUpdatesMapping({ ...updatesMapping, date_col: v })} />
                      <UpdateColPicker label="Contenuto" value={updatesMapping.content_col} headers={updatesHeaders}
                        onChange={(v) => setUpdatesMapping({ ...updatesMapping, content_col: v })} />
                      <UpdateColPicker label="Utente (opz.)" value={updatesMapping.user_col} headers={updatesHeaders}
                        onChange={(v) => setUpdatesMapping({ ...updatesMapping, user_col: v })} />
                    </div>
                  )}
                  {updatesSheet && (
                    <div className="text-[10px] text-slate-600 italic">
                      Match per nome (case-insensitive). Gli aggiornamenti vengono aggiunti alla timeline storica del cliente.
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-center gap-2 text-sm text-slate-700">
                <Sparkles className="w-4 h-4 text-[#B7955B]" />
                <span>Mapping suggerito automaticamente per <strong>{preview.totalRows}</strong> righe.</span>
                <span className="text-slate-400">•</span>
                <span className="text-xs text-slate-500">{Object.keys(mapping).length} colonne mappate</span>
              </div>
              <div className="border border-slate-200 rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left text-[10px] uppercase tracking-wider px-3 py-2 text-slate-600">Colonna nel file</th>
                      <th className="text-left text-[10px] uppercase tracking-wider px-3 py-2 text-slate-600">→</th>
                      <th className="text-left text-[10px] uppercase tracking-wider px-3 py-2 text-slate-600">Mappa nel CRM</th>
                      <th className="text-left text-[10px] uppercase tracking-wider px-3 py-2 text-slate-600">Esempi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(preview.headers || []).map((h, idx) => {
                      const samples = (preview.sample || []).map((r) => r[idx]).filter(Boolean).slice(0, 3);
                      const target = mapping[idx] || "";
                      const isMapped = !!target;
                      return (
                        <tr key={idx} className={isMapped ? "bg-emerald-50/30" : ""} data-testid={`xlsx-row-${idx}`}>
                          <td className="px-3 py-2.5">
                            <div className="font-medium text-slate-900 italic text-slate-400">{h || <span >(vuoto)</span>}</div>
                            <div className="text-[10px] text-slate-400 uppercase tracking-wider">Col {idx + 1}</div>
                          </td>
                          <td className="px-3 py-2.5 text-slate-300"><ArrowRight className="w-4 h-4" /></td>
                          <td className="px-3 py-2.5">
                            <select
                              value={target}
                              onChange={(e) => setColMapping(idx, e.target.value)}
                              data-testid={`xlsx-map-${idx}`}
                              className={`w-full px-2 py-1.5 text-sm border rounded-sm ${
                                isMapped ? "border-emerald-300 bg-emerald-50 text-emerald-900" : "border-slate-300 bg-white"
                              }`}
                            >
                              <option value="">— Ignora colonna —</option>
                              {/* Campi standard del CRM */}
                              <optgroup label="Campi standard">
                                {(preview.targetFields || []).filter((tf) => !tf.isCustom).map((tf) => (
                                  <option
                                    key={tf.key}
                                    value={tf.key}
                                    disabled={Object.values(mapping).includes(tf.key) && mapping[idx] !== tf.key}
                                  >
                                    {tf.label}
                                  </option>
                                ))}
                              </optgroup>
                              {/* Campi custom creati dall'utente */}
                              {(preview.targetFields || []).some((tf) => tf.isCustom) && (
                                <optgroup label="⚡ I tuoi campi personalizzati">
                                  {(preview.targetFields || []).filter((tf) => tf.isCustom).map((tf) => (
                                    <option
                                      key={tf.key}
                                      value={tf.key}
                                      disabled={Object.values(mapping).includes(tf.key) && mapping[idx] !== tf.key}
                                    >
                                      {tf.label}
                                    </option>
                                  ))}
                                </optgroup>
                              )}
                            </select>
                          </td>
                          <td className="px-3 py-2.5 text-xs text-slate-500 max-w-xs">
                            {samples.length > 0 ? samples.join(" • ") : <span className="italic text-slate-300">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* STEP 3: options */}
          {step === 3 && preview && (
            <div className="space-y-5">
              <div className="text-sm text-slate-700">
                Conferma le opzioni di import. Verranno processate <strong>{preview.totalRows}</strong> righe.
              </div>
              <div className="space-y-3 bg-slate-50 border border-slate-200 rounded-sm p-4">
                {/* PORTALE: scelta obbligatoria per separare PP da CQS */}
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1">
                    <span>📁</span> Portale di destinazione <span className="text-rose-500 ml-0.5">*</span>
                  </label>
                  <div className="grid grid-cols-3 gap-2 mt-1.5" data-testid="xlsx-portale-picker">
                    {[
                      { val: "prestiti", label: "💰 Prestiti", color: "#B7955B" },
                      { val: "cessioni", label: "🏛️ Cessioni", color: "#0A192F" },
                      { val: "", label: "📥 Da assegnare", color: "#475569" },
                    ].map((p) => (
                      <button
                        key={p.val || "none"}
                        type="button"
                        onClick={() => setDefaults({ ...defaults, portale: p.val })}
                        data-testid={`xlsx-portale-${p.val || "none"}`}
                        className="px-3 py-2.5 text-[12px] font-bold rounded-sm transition-all"
                        style={{
                          background: defaults.portale === p.val ? p.color : "#fff",
                          color: defaults.portale === p.val ? "#fff" : p.color,
                          border: `2px solid ${p.color}`,
                        }}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-slate-500 mt-1.5 leading-snug">
                    Tutti i {preview.totalRows} clienti importati saranno assegnati a questo portale.
                    Sceglierai "Da assegnare" se preferisci smistarli manualmente in seguito.
                  </p>
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500">Stato di default (se vuoto)</label>
                  <select value={defaults.stato} onChange={(e) => setDefaults({ ...defaults, stato: e.target.value })}
                          data-testid="xlsx-default-stato" className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-sm">
                    <option value="">— Lascia vuoto —</option>
                    {STATI_CLIENTE.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-slate-500">Fonte di default (se vuoto)</label>
                  <input value={defaults.fonte} onChange={(e) => setDefaults({ ...defaults, fonte: e.target.value })}
                         data-testid="xlsx-default-fonte" placeholder="es. Import Excel 28/04/2026"
                         className="w-full mt-1 px-3 py-2 text-sm border border-slate-300 rounded-sm" />
                </div>
                <label className="flex items-center gap-2 cursor-pointer pt-1">
                  <input type="checkbox" checked={defaults.recallToday} onChange={(e) => setDefaults({ ...defaults, recallToday: e.target.checked })}
                         data-testid="xlsx-default-recall" />
                  <span className="text-sm text-slate-700">Imposta recall = oggi (cosi' compaiono in <em>Daily</em>)</span>
                </label>
              </div>
              <div className="text-[11px] text-slate-500 leading-relaxed">
                <strong>Anti-duplicati:</strong> se un cliente esiste gia' (per telefono o CF) viene aggiornato invece di crearne un nuovo.
              </div>
              {/* File grandi: avviso */}
              {preview.totalRows > 5000 && (
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-800">
                  <strong>File di grandi dimensioni:</strong> {preview.totalRows.toLocaleString("it-IT")} righe.
                  L'import in massa puo' richiedere alcuni minuti. Mantieni la scheda aperta finche' non vedi l'esito.
                </div>
              )}
              {loading && (
                <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 space-y-2">
                  <div className="flex items-center gap-2 text-sm text-slate-700">
                    <Loader2 className="w-4 h-4 animate-spin text-[#B7955B]" />
                    <span className="font-medium">
                      {uploadPct < 100 ? `Caricamento file (${uploadPct}%)...` : "Elaborazione lato server..."}
                    </span>
                  </div>
                  <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
                    <div className={`h-full ${uploadPct < 100 ? "bg-[#0A192F]" : "bg-[#B7955B] animate-pulse"}`}
                         style={{ width: `${Math.max(uploadPct, 5)}%`, transition: "width 0.3s" }} />
                  </div>
                  <div className="text-[10px] text-slate-500 italic">
                    {uploadPct >= 100 ? "Il server sta deduplicando e scrivendo i record. Attesa stimata: " + Math.ceil(preview.totalRows / 1500) + "s" : ""}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STEP 4: result */}
          {step === 4 && result && (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mb-4">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h3 className="font-serif text-2xl text-slate-900">Import completato</h3>
              <div className="grid grid-cols-3 gap-4 mt-6 max-w-lg mx-auto">
                <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4">
                  <div className="font-serif text-3xl text-emerald-700 tabular-nums">{result.inserted}</div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-700">Importati</div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
                  <div className="font-serif text-3xl text-amber-700 tabular-nums">{result.skipped}</div>
                  <div className="text-[10px] uppercase tracking-wider text-amber-700">Saltati</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
                  <div className="font-serif text-3xl text-slate-700 tabular-nums">{result.totalProcessed}</div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-700">Totale</div>
                </div>
              </div>
              {result.updates && (
                <div className="mt-6 max-w-lg mx-auto bg-[#F5EFE3]/40 border border-[#B7955B]/40 rounded-sm p-4">
                  <div className="text-[10px] uppercase tracking-wider text-[#8a6e3d] font-semibold mb-2">Aggiornamenti / Timeline storica</div>
                  <div className="grid grid-cols-3 gap-3 text-sm">
                    <div><div className="font-serif text-2xl text-[#0A192F] tabular-nums text-[10px] text-slate-600">{result.updates.imported}</div><div >Aggiornamenti</div></div>
                    <div><div className="font-serif text-2xl text-[#0A192F] tabular-nums text-[10px] text-slate-600">{result.updates.clientsTouched}</div><div >Clienti coinvolti</div></div>
                    <div><div className="font-serif text-2xl text-amber-700 tabular-nums text-[10px] text-slate-600">{result.updates.skipped}</div><div >Saltati</div></div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#F0F2F5] bg-slate-50 flex items-center justify-between">
          <div className="text-xs text-slate-500">{file?.name}</div>
          <div className="flex items-center gap-2">
            {step > 1 && step < 4 && (
              <button onClick={() => setStep(step - 1)} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-white">
                Indietro
              </button>
            )}
            {step === 1 && (
              <button onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-white">Annulla</button>
            )}
            {step === 2 && (
              <button
                onClick={() => setStep(3)}
                disabled={Object.keys(mapping).length === 0}
                data-testid="xlsx-next-2"
                className="px-4 py-2 text-sm text-white rounded-sm disabled:opacity-40 flex items-center gap-2" style={{ background: "var(--navy)" }} onMouseEnter={(e)=>e.currentTarget.style.background="var(--navy-3)"} onMouseLeave={(e)=>e.currentTarget.style.background="var(--navy)"} 
              >
                Avanti <ArrowRight className="w-4 h-4" />
              </button>
            )}
            {step === 3 && (
              <button
                onClick={importNow}
                disabled={loading}
                data-testid="xlsx-import-confirm"
                className="px-5 py-2 text-sm bg-[#B7955B] hover:bg-[#A18048] text-[#0A192F] font-semibold rounded-sm disabled:opacity-60 flex items-center gap-2"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                Importa adesso
              </button>
            )}
            {step === 4 && (
              <button onClick={onClose} className="px-4 py-2 text-sm bg-[#0A192F] text-white rounded-sm">Chiudi</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


function UpdateColPicker({ label, value, headers, onChange }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <select value={value ?? -1} onChange={(e) => onChange(parseInt(e.target.value, 10))}
              className="w-full mt-0.5 px-1.5 py-1 text-xs border border-slate-300 rounded-sm bg-white">
        <option value={-1}>—</option>
        {(headers || []).map((h, i) => (
          <option key={i} value={i}>{i + 1}: {h || "(vuoto)"}</option>
        ))}
      </select>
    </div>
  );
}
